import { createApiService } from './api';

/**
 * Recovery API service for bio + learning path regeneration.
 * Calls the five-user-profile-service recovery endpoints.
 */

const USER_PROFILE_API_BASE_URL =
  import.meta.env.VITE_FIVE_USER_PROFILE_SERVICE_URL ||
  import.meta.env.FIVE_USER_PROFILE_SERVICE_URL ||
  '/api/user-profile';

const recoveryClient = createApiService(USER_PROFILE_API_BASE_URL);

export interface RecoveryStatus {
  userId: string;
  bioComplete: boolean;
  learningPathExists: boolean;
  activePlanExists: boolean;
  hasSurveyData: boolean;
  onboardingSessionId: string | null;
  recoveryActions: string[]; // NEEDS_SURVEY, NEEDS_BIO, NEEDS_LEARNING_PATH
  /** Question IDs needed for recovery survey (from backend, not hardcoded). */
  requiredQuestionIds: string[];
}

export interface RegenerateRequest {
  sessionId: string;
  regenerateBio?: boolean;
  regenerateLearningPath?: boolean;
  selectedPainPointId?: string;
  selectedWinIds?: string[];
  selectedPathPlan?: string;
}

export interface RegenerateResponse {
  status: string; // PROCESSING, COMPLETED, FAILED, PARTIAL
  message?: string;
  correlationId?: string;
  bioGenerated?: boolean;
  learningPathGenerated?: boolean;
  errorMessage?: string;
}

export const recoveryApi = {
  /** Check what's missing for a user. */
  getStatus: async (userId: string): Promise<RecoveryStatus> => {
    return recoveryClient.get<RecoveryStatus>(
      `/api/v1/recovery/${encodeURIComponent(userId)}/status`
    );
  },

  /** Create a lightweight recovery session for re-survey. */
  createSession: async (
    userId: string,
    source: string = 'PROFILE_PAGE'
  ): Promise<{ sessionId: string; userId: string; type: string }> => {
    return recoveryClient.post(
      `/api/v1/recovery/${encodeURIComponent(userId)}/session`,
      { source }
    );
  },

  /** Trigger bio and/or learning path regeneration. */
  regenerate: async (
    userId: string,
    request: RegenerateRequest
  ): Promise<RegenerateResponse> => {
    return recoveryClient.post<RegenerateResponse>(
      `/api/v1/recovery/${encodeURIComponent(userId)}/regenerate`,
      request
    );
  },

  /** Poll regeneration status. */
  getRegenerateStatus: async (
    userId: string,
    correlationId?: string
  ): Promise<RegenerateResponse> => {
    const params = correlationId ? `?correlationId=${correlationId}` : '';
    return recoveryClient.get<RegenerateResponse>(
      `/api/v1/recovery/${encodeURIComponent(userId)}/regenerate/status${params}`
    );
  },
};
